import { ICategory, ICategoryType, IHobby, ISkill, ISubject } from '@wot/typings';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import categoryApi from '../../api/categories';
import postRequirementApi from '../../api/postRequirement';
import { POST_REQUIREMENT } from '../../assets/images';
import { useAppSelector } from '../../hooks';
import Step1 from './Step1';
import Step2, { TutorCategory } from './Step2';
import { SEO } from '../../components/SEO';

export interface AddRequirement {
  board?: string;
  grade?: string;
  type?: string;
  level?: string;
  category: TutorCategory;
  categoryType: string;
  categoryList: string[];
  userId: string;
}

export default function PostRequirement() {
  const [step, setStep] = useState<'step-1' | 'step-2'>('step-1');
  const [selected, setSelected] = useState<ICategoryType>('subject');
  const [requirementData, setRequirementData] = useState<AddRequirement>();
  const [categoryData, setCategoryData] = useState<ICategory<ISubject | IHobby | ISkill>[]>([]);
  const { userData } = useAppSelector((state) => state.userSlice);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setRequirementData(undefined);
  }, [selected]);

  useEffect(() => {
    const data = localStorage.getItem('RequirementData');
    if (data) {
      setRequirementData(JSON.parse(data));
      setStep('step-2');
    }
  }, []);

  useEffect(() => {
    categoryApi.getCategoriesByType(selected).then((data) => {
      setCategoryData(data);
    });
  }, [selected]);

  const handleStep1 = (data: any) => {
    setRequirementData({
      ...requirementData,
      ...data
    });
    setStep('step-2');
  };

  const handleStep2 = (data: any) => {
    setRequirementData({
      ...requirementData,
      ...data
    });
    if (userData) {
      if (requirementData) {
        postRequirementApi
          .add(userData._id, {
            ...requirementData,
            ...data,
            userId: userData._id
          })
          .then(() => {
            navigate('/requirements');
            const localData = localStorage.getItem('RequirementData');
            if (localData) {
              localStorage.removeItem('RequirementData');
            }
          });
      }
    } else {
      localStorage.setItem('RequirementData', JSON.stringify({ ...requirementData, ...data }));
      localStorage.setItem('prevPath', location.pathname);
      navigate('/login');
    }
  };
  return (
    <>
    <SEO 
      title="Post Your Requirement - Find Your Perfect Tutor"
      description="Post your tutoring requirements and find the perfect tutor for your needs. Tell us about your learning goals, subjects, and schedule."
      keywords="post requirement, find tutor, tutoring needs, learning goals, education requirements"
      url="/post-requirement"
      type="website"
      og={{
        type: 'website',
        siteName: 'WorldoTutor',
        locale: 'en_US',
        image: {
          url: '/imgi_1_favicon.png',
          alt: 'Post Your Requirement - WorldoTutor',
          width: 1200,
          height: 630
        }
      }}
      twitter={{
        card: 'summary_large_image',
        site: '@worldotutor',
        image: {
          url: '/imgi_1_favicon.png',
          alt: 'Post Your Requirement - WorldoTutor'
        }
      }}
    />
    <div className="w-full flex lg:flex-row h-screen">
      <div className="lg:bg-primary lg:w-1/2 lg:h-full lg:flex hidden">
        <div className="w-full h-full flex flex-col items-center justify-evenly">
          <div className="w-3/5 mx-auto">
            <div className="w-full bg-white h-1.5 rounded">
              <div
                className={`bg-secondary h-1.5 ${step === 'step-1' ? 'w-1/2' : ''} ${step === 'step-2' ? 'w-full' : ''
                  } rounded`}
              ></div>
            </div>
            <div className="flex justify-between mt-3">
              <span className="text-lg font-bold text-white cursor-pointer" onClick={() => setStep('step-1')}>
                Select Goal
              </span>
              <span className="text-lg font-bold text-white cursor-pointer" onClick={() => setStep('step-2')}>
                Tutor Category
              </span>
            </div>
          </div>
          <div className="h-72">
            <img src={POST_REQUIREMENT} alt="Post requirement cover" className="w-full h-full object-contain" />
          </div>
          <div className="w-4/5 flex flex-col">
            <h1 className="text-4xl leading-snug font-bold text-white">
              Need help in finding the best tutor matching your requirements?
            </h1>
            <h4 className="text-lg font-normal text-white w-[70%] mt-5">
              Submit your requirement and we will get back to you with the best teacher.
            </h4>
          </div>
        </div>
      </div>
      <div className="flex lg:w-1/2 w-full h-full items-center justify-center bg-[#F5F6F9]">
        <div className="flex flex-col lg:px-12 px-6 lg:w-4/5 container min-h-[90%] bg-white rounded-xl md:border border-neutralBlue1 md:shadow-md">
          {step === 'step-1' ? (
            <Step1
              setStep={setStep}
              selected={selected}
              setSelected={setSelected}
              handleStep1={handleStep1}
              requirementData={requirementData}
              categoryData={categoryData}
            />
          ) : (
            <Step2 handleStep2={handleStep2} setStep={setStep} requirementData={requirementData} />
          )}
        </div>
      </div>
    </div>
    </>
  );
}
